import { useState } from "react";
import { ChevronDown, HelpCircle, AlertCircle, Scale, Activity, Shield, Briefcase, Beaker, FileText, Stethoscope, ShoppingCart } from "lucide-react";

const FAQ = () => {
  const [openItems, setOpenItems] = useState([]);

  const toggleItem = (index) => {
    if (openItems.includes(index)) {
      setOpenItems(openItems.filter((i) => i !== index));
    } else {
      setOpenItems([...openItems, index]);
    }
  };

  const faqData = [
    {
      icon: HelpCircle,
      question: "Promil hesaplaması ne kadar doğru?",
      answer: "Hesaplamalar bilimsel Widmark formülüne dayanır. Ancak metabolizma, sağlık durumu, mide doluluğu, yaş gibi faktörler kişiden kişiye farklı olabileceğinden bu sonuç teorik bir tahmindir. Resmi ölçüm yerine geçmez; en kesin sonuç Alcodot ile elde edilir."
    },
    {
      icon: Activity,
      question: "Neden promil değerim hesaplanandan farklı çıkabilir?",
      answer: "Vücut alkolü her insanda farklı hızda işler. Yemek durumu, su tüketimi, ilaçlar ve stres gibi birçok faktör promili etkiler. Bu nedenle teorik hesaplama ile gerçek değer arasında fark olabilir."
    },
    {
      icon: AlertCircle,
      question: "0.50 promil neden bu kadar kritik?",
      answer: "Türkiye'de hususi araç sürücüleri için yasal sınır 0.50 promildir.\nBu sınırın üzeri:\n\n• 9.268 TL para cezası\n• 6 ay ehliyetin alınması\n• Trafik güvenliğini tehlikeye sokma riski\n\ngibi sonuçlar doğurur. Bu nedenle kritik eşiğin altına inmek çok önemlidir."
    },
    {
      icon: AlertCircle,
      question: "0.40–0.49 promil arası neden riskli?",
      answer: "Çünkü teorik olarak 0.40–0.49 promil görünen bir değer, kişiye bağlı faktörlerle gerçekte 0.50'nin üzerine çıkabilir. Bu yüzden bu aralık \"tam sınır bölgesi\" olarak kabul edilir ve araç kullanmak risklidir."
    },
    {
      icon: Scale,
      question: "1.00 promil ve üzeri neden adli süreç doğurabiliyor?",
      answer: "1.00 promil üzeri çok yüksek seviye kabul edilir. Bu seviyede alkol + kaza veya tehlikeli sürüş durumu birleşirse TCK 179 Trafik Güvenliğini Tehlikeye Sokma kapsamında savcılık süreci, ifade alma ve adli soruşturma başlayabilir. Sadece idari ceza ile sınırlı kalmayabilir."
    },
    {
      icon: Briefcase,
      question: "Ticari sürücüler için neden sınır 0.00?",
      answer: "Taksi, servis, kamyon, tır ve toplu taşıma gibi araçlarda sıfır tolerans uygulanır. Yolcu ve yük güvenliği nedeniyle ticari sürücülerin kanında hiç alkol bulunmamalıdır."
    },
    {
      icon: Shield,
      question: "Alcodot nasıl çalışıyor?",
      answer: "Alcodot, dil altına yerleştirilen hızlı sonuç veren bir test çubuğudur.\n\n• 10 saniyede sonuç verir\n• 0.50 promil ve üzeri durumlarda turkuaz renge döner\n• Tek kullanımlıktır\n• Cüzdan/çantada kolay taşınır"
    },
    {
      icon: Beaker,
      question: "Test çubuğunun rengi hafif değişirse ne yapmalıyım?",
      answer: "Bu genellikle sınırda bir seviyeyi gösterir.\n\n• 1 dakika sonra tekrar test edin\n• Belirsizlik devam ediyorsa araç kullanmayın\n• Kesin bilgi için yeni bir çubukla tekrar test edin"
    },
    {
      icon: FileText,
      question: "Bu hesaplama resmi delil midir?",
      answer: "Hayır. Bu hesaplama bir tahmindir. Resmi işlemlerde yalnızca trafik ekiplerinin yaptığı ölçüm geçerlidir."
    },
    {
      icon: Stethoscope,
      question: "Tıbbi durumlar promili etkiler mi?",
      answer: "Evet. Karaciğer hastalıkları, ilaçlar, diyabet, mide operasyonları gibi durumlar alkol metabolizmasını etkileyebilir. Bu nedenle aynı miktar farklı kişilerde farklı promil değerleri üretebilir."
    },
    {
      icon: ShoppingCart,
      question: "Alcodot'u nereden satın alabilirim?",
      answer: "Sonuç ekranı altındaki \"Riske Atma — Alcodot ile %100 Emin Ol\" butonuna tıklayarak satın alma sayfasına ulaşabilirsiniz."
    }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="faq-section">
      {/* Header */}
      <div className="text-center mb-12 fade-in">
        <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
          Sık Sorulan Sorular
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Alkol promil hesaplama ve Alcodot hakkında merak ettikleriniz
        </p>
      </div>

      {/* FAQ Items */}
      <div className="space-y-4">
        {faqData.map((item, index) => {
          const isOpen = openItems.includes(index);
          const Icon = item.icon;

          return (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md smooth-transition"
              data-testid={`faq-item-${index}`}
            >
              {/* Question Button */}
              <button
                onClick={() => toggleItem(index)}
                className={`w-full px-6 py-5 flex items-center justify-between text-left smooth-transition hover:bg-gray-50 ${
                  isOpen ? "bg-gradient-to-r from-teal-50 to-cyan-50" : ""
                }`}
                data-testid={`faq-question-${index}`}
              >
                <div className="flex items-center gap-4 flex-1">
                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center smooth-transition ${
                      isOpen
                        ? "bg-gradient-to-br from-teal-500 to-cyan-600 shadow-lg"
                        : "bg-gray-100"
                    }`}
                    style={
                      isOpen
                        ? { boxShadow: "0 4px 12px rgba(20, 184, 166, 0.3)" }
                        : {}
                    }
                  >
                    <Icon
                      className={`w-5 h-5 smooth-transition ${
                        isOpen ? "text-white" : "text-gray-600"
                      }`}
                    />
                  </div>

                  {/* Question Text */}
                  <h3
                    className={`text-lg font-bold smooth-transition ${
                      isOpen ? "text-teal-700" : "text-gray-900"
                    }`}
                  >
                    {item.question}
                  </h3>
                </div>

                {/* Chevron */}
                <ChevronDown
                  className={`w-6 h-6 text-gray-400 smooth-transition flex-shrink-0 ml-4 ${
                    isOpen ? "rotate-180 text-teal-600" : ""
                  }`}
                  style={{ transition: "transform 0.3s ease-out" }}
                />
              </button>

              {/* Answer */}
              <div
                className={`overflow-hidden smooth-transition ${
                  isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                }`}
                style={{
                  transition: "max-height 0.35s ease-out, opacity 0.3s ease-out",
                }}
              >
                <div className="px-6 pb-6 pt-2">
                  <div className="pl-14">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <div className="mt-12 text-center bg-gradient-to-r from-teal-500 to-cyan-600 rounded-2xl p-8 shadow-lg">
        <p className="text-white text-lg font-bold mb-3">
          Daha fazla sorunuz mu var?
        </p>
        <p className="text-teal-50 text-sm mb-5">
          WhatsApp destek hattımızdan bize ulaşabilirsiniz
        </p>
        <button
          onClick={() => {
            const element = document.querySelector('[data-testid="whatsapp-toggle-btn"]');
            if (element) element.click();
          }}
          className="bg-white text-teal-600 font-bold px-8 py-3 rounded-xl hover:bg-teal-50 smooth-transition shadow-md"
        >
          Destek Al
        </button>
      </div>
    </div>
  );
};

export default FAQ;
